/**
 * Reconciliation — the moat. Where disagreements with the incumbent get resolved, not
 * just surfaced. Three tabs: Definition reconciliation (theirs ≠ ours, Δ decomposed,
 * sign-off writes the registry), Parity ledger (finance excluded per KAL-385), Margin
 * bridge. REPRESENTATIVE view-model behind an honest chip; sign-off is local for now.
 */

import React, { useState } from 'react'
import SurfaceHeader from '../components/kit/SurfaceHeader'
import RepresentativeChip from '../components/kit/RepresentativeChip'
import Waterfall from '../components/kit/charts/Waterfall'
import Heatmap, { type HeatCell } from '../components/kit/charts/Heatmap'
import { useTokens } from '../components/kit/charts/useTokens'
import { gbpK } from '../viewmodels/format'
import { definition, parityCols, parityRows, parityPass, marginBridge } from '../viewmodels/reconciliation'

const CARD = 'border border-line rounded-xl bg-panel overflow-hidden'
const HEAD = 'px-4 py-3 border-b border-line2 font-semibold text-[13.5px] text-ink'
const TABS = [
  { id: 'definition', label: 'Definition reconciliation' },
  { id: 'parity', label: 'Parity ledger' },
  { id: 'bridge', label: 'Margin bridge' },
]

function SideCard({ d, tone }: { d: { title: string; sub: string; value: string; grain: string }; tone: string }) {
  return (
    <div className="flex-1 border border-line rounded-xl px-5 py-4 bg-panel min-w-[220px]">
      <div className="font-mono text-[10px] tracking-[.1em] uppercase text-mut2">{d.title}</div>
      <div className="text-[12px] text-mut mt-1 mb-3.5">{d.sub}</div>
      <div className="text-[30px] font-bold tracking-[-.02em] tabular-nums" style={{ color: tone }}>{d.value}</div>
      <div className="mt-2.5 font-mono text-[11px] text-mut">{d.grain}</div>
    </div>
  )
}

function DefinitionTab({ t }: { t: Record<string, string> }) {
  const [signed, setSigned] = useState(false)
  return (
    <div className="space-y-4">
      <div className="flex gap-3.5 items-stretch flex-wrap">
        <SideCard d={definition.theirs} tone={t.ink} />
        <div className="flex items-center text-[20px] text-mut2">≠</div>
        <SideCard d={definition.ours} tone={t.good} />
      </div>
      <div className={CARD}>
        <div className={HEAD}>Δ +3.7pp decomposed into its causes</div>
        <div className="px-3 py-4"><Waterfall steps={definition.bridge} posColor={t.purple} fmt={(v) => v.toFixed(1)} /></div>
        <div className="flex gap-2.5 flex-wrap px-4 pb-4">
          {definition.effects.map((e) => (
            <div key={e.key} className="flex-1 min-w-[180px] px-3.5 py-3 border border-line2 rounded-lg">
              <div className="font-mono text-[10px] text-purple">{e.key}</div>
              <div className="text-[12px] text-mut mt-1">{e.note}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3.5 px-4 py-4 rounded-xl border" style={{ borderColor: signed ? 'var(--good)' : 'var(--brand)', background: signed ? 'var(--good-bg)' : 'var(--info-bg)' }}>
        <div className="flex-1">
          <div className="font-bold" style={{ color: signed ? 'var(--good)' : 'var(--brand)' }}>{signed ? 'Signed — the standard is now the registry truth' : 'Sign off writes the registry'}</div>
          <div className="text-[12.5px] text-mut mt-0.5">{signed ? 'Sign-off chips flipped to “signed” app-wide. Restatement logged.' : 'Committing the standard flips sign-off chips app-wide and logs the decision. This is an action, not a note.'}</div>
        </div>
        <button onClick={() => setSigned((s) => !s)} className="cx-btn px-5 py-2.5 rounded-lg text-white font-semibold text-[13px]" style={{ background: signed ? 'var(--good)' : 'var(--brand)', border: 'none' }}>
          {signed ? '✓ Signed · undo' : '✎ Sign off definition'}
        </button>
      </div>
    </div>
  )
}

function ParityTab({ t }: { t: Record<string, string> }) {
  const getCell = (r: { label: string; excl?: boolean }, c: string): HeatCell => {
    if (r.excl) return { v: 'excl', bg: t.panel2, fg: t.mut2 }
    const pass = parityPass(r.label, c)
    return { v: pass ? '✓' : '✕', bg: pass ? 'rgba(16,185,129,.5)' : 'rgba(239,68,68,.55)', fg: pass ? '#03130d' : '#fff' }
  }
  return (
    <div className={CARD}>
      <div className={HEAD}>Parity ledger · Cortex vs served master views <span className="font-mono text-[10px] text-mut2 font-normal">· audits the incumbent’s claimed Diff = 0</span></div>
      <div className="cx-scroll px-4 py-4 overflow-x-auto"><Heatmap rows={parityRows} cols={parityCols} getCell={getCell} format={(c) => String(c.v)} /></div>
      <div className="px-4 pb-4 font-mono text-[10.5px] text-mut2">Finance metrics (OPEX, EBITDA) flagged excluded from parity until KAL-385 is fixed — a designed state, not a blank.</div>
    </div>
  )
}

export default function Reconciliation(): React.JSX.Element {
  const t = useTokens()
  const [tab, setTab] = useState('definition')
  return (
    <div className="cx-fade cx-page py-6 pb-20 space-y-4">
      <SurfaceHeader
        kicker="The moat · dedicated surface"
        kickerColor="var(--pink)"
        title="Reconciliation"
        desc="Where disagreements with the incumbent get resolved, not just surfaced. Nothing in Power BI can do this — it is the SOW's KPI-framework deliverable."
      />
      <RepresentativeChip label="representative — pending parity backend" />
      <div className="flex gap-1.5">
        {TABS.map((r) => (
          <button key={r.id} onClick={() => setTab(r.id)} className={`cx-btn px-3 py-1.5 rounded-lg border text-[12.5px] ${tab === r.id ? 'border-brand bg-info-bg text-brand' : 'border-line bg-panel text-mut'}`}>
            {r.label}
          </button>
        ))}
      </div>
      {tab === 'definition' && <DefinitionTab t={t} />}
      {tab === 'parity' && <ParityTab t={t} />}
      {tab === 'bridge' && (
        <div className={CARD}>
          <div className={HEAD}>Margin bridge · floor ↔ ledger ↔ as-reported</div>
          <div className="px-3 py-4"><Waterfall steps={marginBridge} fmt={gbpK} /></div>
          <div className="px-4 pb-4 text-[12.5px] text-mut">Each leg is a governed adjustment with an owner and a lineage — the exec can see exactly where the floor number becomes the board number.</div>
        </div>
      )}
    </div>
  )
}
